import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { ApiError } from '@/lib/apiClient'
import { formatDateTime } from '@/lib/datetime'
import { cn } from '@/lib/cn'

import { useAdminMutation, useAdminQuery } from '../api'
import { useAdminContext } from '../session'
import type { Page } from '../useCollection'
import type { ConfirmationResult, TableRequest, TableTypeOption } from '../types'

/**
 * La suite d'une table sur mesure acceptée : convives, règlement, confirmation.
 *
 * Un cas réel l'a fait naître : une table pointée payée, et un client qui a
 * reçu une confirmation sans QR ni noms. D'où trois choses, dans l'ordre où
 * on les fait :
 *
 * 1. **Les convives** — les prénoms donnés au formulaire, complétés ici. Ce
 *    sont eux que l'accueil et l'e-mail liront.
 * 2. **Le règlement** — « Marquer payé », là où l'on suit la demande.
 * 3. **La confirmation** — un bouton à côté, actif une fois la table réglée :
 *    remerciements, tous les noms, le QR. Le pointage ne l'envoie pas tout
 *    seul ; il la propose aussitôt (« Envoyer la confirmation maintenant ? »).
 *
 * `withMarkPaid` à faux sert à l'écran « Commandes », qui a déjà son propre
 * encaissement : on n'y affiche pas deux fois le même geste.
 */
const OFFLINE_METHODS = ['paypal', 'transfer', 'cash', 'manual']

export function TableRequestFollowUp({
  request,
  withMarkPaid = true,
  withBackfill = true,
}: {
  request: TableRequest
  withMarkPaid?: boolean
  /** Faux sur « Commandes » : « Définir le type de table » y fait le même geste. */
  withBackfill?: boolean
}) {
  const { t, i18n } = useTranslation()
  const { eventId, event, can } = useAdminContext()
  const timeZone = event?.timezone ?? 'UTC'
  const base = `/admin/events/${eventId}/table-requests/${request.id}`
  const invalidate = [
    ['admin-table-requests', eventId],
    ['admin-orders', eventId],
    ['admin-table-request-for-order', eventId, request.order_id ?? ''],
  ]

  const saveGuests = useAdminMutation<TableRequest, { guest_names: string[] }>(
    'put', () => `${base}/guests`, eventId, invalidate,
  )
  const markPaid = useAdminMutation<unknown, { method: string; reference: string | null }>(
    'post',
    () => `/admin/events/${eventId}/orders/${request.order_id}/mark-paid`,
    eventId,
    invalidate,
  )
  const send = useAdminMutation<ConfirmationResult, Record<string, never>>(
    'post', () => `${base}/send-confirmation`, eventId, invalidate,
  )

  const seats = Math.max(request.party_size - 1, 0)
  const [guests, setGuests] = useState<string[]>(() => padded(request.guest_names, seats))
  const [paying, setPaying] = useState(false)
  const [method, setMethod] = useState(OFFLINE_METHODS[0] ?? 'paypal')
  const [reference, setReference] = useState('')
  // Juste après « Marquer payé » : la question qu'on se pose aussitôt.
  const [prompt, setPrompt] = useState(false)

  // Les prénoms enregistrés ailleurs (autre onglet, autre écran) reviennent
  // dans les cases tant qu'on n'est pas en train de les modifier.
  const saved = request.guest_names.join('\n')
  useEffect(() => {
    setGuests(padded(request.guest_names, seats))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved, seats])

  const dirty =
    guests.map((name) => name.trim()).filter(Boolean).join('\n') !== saved
  const paid = request.order_status === 'paid'
  // Une table que le client règle à l'entrée : sa confirmation (QR, noms,
  // montant à régler) part avant la soirée, sans attendre l'encaissement.
  const atDoor =
    request.order_status === 'pending_payment' &&
    (request.payment_choice === 'cash_at_door' || request.payment_choice === 'paypal_at_door')
  const sendable = paid || atDoor
  const closed = request.order_status === 'cancelled' || request.order_status === 'refunded'
  const canWrite = can('tables.reservation.update')
  const canPay = withMarkPaid && can('orders.payment.mark_paid')
  const busy = saveGuests.isPending || markPaid.isPending || send.isPending
  const error = saveGuests.error ?? markPaid.error ?? send.error
  const result = send.data

  return (
    <section className="mt-4 space-y-4 border-t border-line-subtle pt-4">
      {request.admission_ticket_missing ? (
        <p
          role="alert"
          className="rounded-md border border-state-warning/40 bg-state-warning/5 px-3 py-2 text-sm text-state-warning"
        >
          {t('admin.tableRequests.admissionMissing')}{' '}
          <Link to="../reglages" relative="path" className="underline">
            {t('admin.tableRequests.admissionMissingLink')}
          </Link>
        </p>
      ) : null}

      {request.needs_reservation && withBackfill ? <BackfillReservation request={request} /> : null}

      {/* Les convives : celui qui demande, puis une case par place. */}
      <div>
        <p className="text-caption uppercase tracking-wide text-content-muted">
          {t('admin.tableRequests.guestsTitle', { count: request.party_size })}
        </p>
        <p className="mt-1 text-sm text-content-primary">
          {request.full_name}{' '}
          <span className="text-caption text-content-muted">
            {t('admin.tableRequests.requester')}
          </span>
        </p>
        {seats > 0 ? (
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {guests.map((name, index) => (
              <input
                key={index}
                value={name}
                maxLength={120}
                disabled={!canWrite || closed}
                aria-label={t('admin.tableRequests.guestName', { number: index + 2 })}
                placeholder={t('admin.tableRequests.guestName', { number: index + 2 })}
                onChange={(changeEvent) =>
                  setGuests((current) =>
                    current.map((entry, position) =>
                      position === index ? changeEvent.target.value : entry,
                    ),
                  )
                }
                className={INPUT}
              />
            ))}
          </div>
        ) : null}
        {canWrite && seats > 0 && !closed ? (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              variant="outline"
              disabled={busy || !dirty}
              onClick={() =>
                saveGuests.mutate({
                  guest_names: guests.map((entry) => entry.trim()).filter(Boolean),
                })
              }
            >
              {t('admin.tableRequests.saveGuests')}
            </Button>
            {saveGuests.isSuccess && !dirty ? (
              <span className="text-caption text-state-success">
                {t('admin.tableRequests.guestsSaved')}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Le règlement, puis la confirmation, côte à côte. */}
      {request.order_id ? (
        <div>
          <p className="text-sm text-content-secondary">
            {paid && request.order_paid_at
              ? t('admin.tableRequests.paidOn', {
                  date: formatDateTime(request.order_paid_at, i18n.language, timeZone),
                })
              : closed
                ? t(`admin.orders.status.${request.order_status}`)
                : atDoor
                  ? t('admin.orders.atDoor.due')
                  : t('admin.tableRequests.awaitingPayment')}
          </p>

          {paying && !paid ? (
            <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,12rem)_minmax(0,1fr)_auto] sm:items-end">
              <label className="text-caption text-content-muted">
                {t('admin.orders.method')}
                <select
                  value={method}
                  onChange={(changeEvent) => setMethod(changeEvent.target.value)}
                  className={INPUT}
                >
                  {OFFLINE_METHODS.map((value) => (
                    <option key={value} value={value}>
                      {t(`admin.orders.methods.${value}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-caption text-content-muted">
                {method === 'paypal'
                  ? t('admin.orders.referencePaypal')
                  : t('admin.orders.reference')}
                <input
                  value={reference}
                  onChange={(changeEvent) => setReference(changeEvent.target.value)}
                  className={INPUT}
                />
              </label>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    markPaid.mutate(
                      { method, reference: reference.trim() || null },
                      {
                        onSuccess: () => {
                          setPaying(false)
                          setPrompt(true)
                        },
                      },
                    )
                  }
                >
                  {t('admin.orders.confirmPaid')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setPaying(false)}>
                  {t('admin.project.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {canPay && !paid && !closed ? (
                <Button size="sm" variant="outline" disabled={busy} onClick={() => setPaying(true)}>
                  {t('admin.orders.markPaid')}
                </Button>
              ) : null}
              {canWrite && !closed ? (
                <Button
                  size="sm"
                  variant={sendable && !request.confirmation_sent_at ? 'champagne' : 'outline'}
                  disabled={busy || !sendable}
                  title={sendable ? undefined : t('admin.tableRequests.sendNeedsPaid')}
                  onClick={() => {
                    setPrompt(false)
                    send.mutate({})
                  }}
                >
                  {send.isPending
                    ? t('admin.tableRequests.sending')
                    : request.confirmation_sent_at
                      ? t('admin.tableRequests.resendConfirmation')
                      : t('admin.tableRequests.sendConfirmation')}
                </Button>
              ) : null}
            </div>
          )}

          {!paid && !closed && canWrite ? (
            <p className="mt-2 text-caption text-content-muted">
              {t('admin.tableRequests.sendNeedsPaid')}
            </p>
          ) : null}

          {request.confirmation_sent_at ? (
            <p className="mt-2 text-caption text-content-muted">
              {t('admin.tableRequests.lastSent', {
                date: formatDateTime(request.confirmation_sent_at, i18n.language, timeZone),
                who: request.confirmation_sent_by_label ?? '—',
              })}
            </p>
          ) : paid ? (
            <p className="mt-2 text-caption text-state-warning">
              {t('admin.tableRequests.notSentYet')}
            </p>
          ) : null}

          {prompt && paid && canWrite ? (
            <div
              role="status"
              className="mt-3 flex flex-wrap items-center gap-3 rounded-md border border-champagne-400/40 bg-champagne-400/5 px-3 py-2"
            >
              <span className="text-sm text-content-primary">
                {t('admin.tableRequests.sendNowPrompt')}
              </span>
              <Button
                size="sm"
                variant="champagne"
                disabled={busy}
                onClick={() => {
                  setPrompt(false)
                  send.mutate({})
                }}
              >
                {t('admin.tableRequests.sendNowYes')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setPrompt(false)}>
                {t('admin.tableRequests.sendNowLater')}
              </Button>
            </div>
          ) : null}

          {result ? (
            <p
              role="status"
              className={cn(
                'mt-2 text-sm',
                result.delivery === 'sent' ? 'text-state-success' : 'text-state-danger',
              )}
            >
              {t(`admin.tableRequests.delivery.${result.delivery}`, {
                email: result.recipient,
                error: result.error ?? '',
              })}
            </p>
          ) : null}

          <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
            <Link
              to={`../reservations?open=${request.order_id}`}
              relative="path"
              className="text-champagne-300 underline"
            >
              {t('admin.tableRequests.openReservation')}
            </Link>
            <Link to="../placement" relative="path" className="text-champagne-300 underline">
              {t('admin.tableRequests.openPlacement')}
            </Link>
          </p>
        </div>
      ) : null}

      {error instanceof ApiError ? (
        <p className="text-sm text-state-danger">{error.message}</p>
      ) : null}
    </section>
  )
}

/**
 * Depuis « Commandes » : la demande dont est née cette commande, s'il y en a
 * une. Rien ne s'affiche pour une commande ordinaire.
 */
export function TableRequestOrderPanel({ orderId }: { orderId: string }) {
  const { eventId } = useAdminContext()
  const query = useAdminQuery<Page<TableRequest>>(
    ['admin-table-request-for-order', eventId, orderId],
    `/admin/events/${eventId}/table-requests?order_id=${orderId}`,
    eventId,
  )
  const request = query.data?.items[0]
  if (!request) return null
  return <TableRequestFollowUp request={request} withMarkPaid={false} withBackfill={false} />
}

/**
 * Une table acceptée avant que l'acceptation crée sa réservation.
 *
 * Cas réel : acceptée et réglée, elle n'avait qu'une commande — ni
 * réservation, ni convives. Absente du placement, l'hôtesse ne pouvait pas
 * l'asseoir. On lui donne ici ce que l'acceptation donne aujourd'hui : un type
 * (« Sur mesure » d'office), un nom de groupe, les bouteilles du type, les
 * convives. Réglée, elle devient ferme et reçoit son QR. Aucun montant ne bouge.
 */
function BackfillReservation({ request }: { request: TableRequest }) {
  const { t } = useTranslation()
  const { eventId, can } = useAdminContext()
  const base = `/admin/events/${eventId}/table-requests`
  const tableTypes = useAdminQuery<TableTypeOption[]>(
    ['admin-table-request-types', eventId], `${base}/table-types`, eventId,
  )
  const create = useAdminMutation<TableRequest, { table_type_id: string | null; name: string | null }>(
    'post',
    () => `${base}/${request.id}/reservation`,
    eventId,
    [
      ['admin-table-requests', eventId],
      ['admin-orders', eventId],
      ['admin-table-request-for-order', eventId, request.order_id ?? ''],
      ['admin-reservations', eventId],
    ],
  )

  const options = tableTypes.data ?? []
  const fitting = options.filter(
    (option) =>
      option.capacity_min <= request.party_size && request.party_size <= option.capacity_max,
  )
  const suggested = options.find((option) => option.suggested) ?? fitting[0] ?? options[0] ?? null
  const [tableTypeId, setTableTypeId] = useState('')
  const [groupName, setGroupName] = useState('')
  const chosenId = tableTypeId || suggested?.id || ''
  const chosen = options.find((option) => option.id === chosenId) ?? null
  const outOfRange =
    chosen !== null &&
    (request.party_size < chosen.capacity_min || request.party_size > chosen.capacity_max)
  const canWrite = can('tables.reservation.update')

  return (
    <div
      role="alert"
      className="space-y-3 rounded-md border border-state-warning/40 bg-state-warning/5 px-3 py-3"
    >
      <p className="text-sm text-state-warning">{t('admin.tableRequests.backfill.notice')}</p>
      {canWrite ? (
        <>
          {options.length === 0 && !tableTypes.isPending ? (
            <p className="text-sm text-state-warning">{t('admin.tableRequests.noTableType')}</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-caption text-content-muted">
                {t('admin.tableRequests.tableType')}
                <select
                  value={chosenId}
                  onChange={(changeEvent) => setTableTypeId(changeEvent.target.value)}
                  className={INPUT}
                >
                  {options.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label} ({option.capacity_min}–{option.capacity_max})
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-caption text-content-muted">
                {t('admin.tableRequests.groupName')}
                <input
                  value={groupName}
                  maxLength={120}
                  placeholder={t('admin.tableRequests.groupNamePlaceholder')}
                  onChange={(changeEvent) => setGroupName(changeEvent.target.value)}
                  className={INPUT}
                />
              </label>
            </div>
          )}
          {outOfRange && chosen ? (
            <p className="text-sm text-state-warning">
              {t('admin.tableRequests.typeCapacity', {
                min: chosen.capacity_min,
                max: chosen.capacity_max,
                count: request.party_size,
              })}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              variant="champagne"
              disabled={create.isPending || !chosen || outOfRange}
              onClick={() =>
                create.mutate({ table_type_id: chosenId || null, name: groupName.trim() || null })
              }
            >
              {create.isPending
                ? t('admin.tableRequests.backfill.creating')
                : t('admin.tableRequests.backfill.create')}
            </Button>
            <span className="text-caption text-content-muted">
              {t('admin.tableRequests.backfill.moneyUnchanged')}
            </span>
          </div>
          {create.error instanceof ApiError ? (
            <p className="text-sm text-state-danger">{create.error.message}</p>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

function padded(names: string[], seats: number): string[] {
  return Array.from({ length: seats }, (_, index) => names[index] ?? '')
}

const INPUT =
  'mt-1 h-10 w-full min-w-0 rounded-md border border-line bg-bg-base px-3 text-sm text-content-primary disabled:opacity-60'
